import React, { forwardRef, memo } from 'react'

import Select, { GroupBase, Props as SelectProps, StylesConfig } from 'react-select'

import { Box } from '@/atoms/layout'
import { SelectOption } from '@/models/common'

import { getCustomSelectStyle } from './atoms'

export type MenuPlacement = 'auto' | 'bottom' | 'top'
export type MenuPosition = 'absolute' | 'fixed'

interface OwnProps {
  hasError?: boolean
  customStyle?: StylesConfig<SelectOption, false>
  menuHeight?: number
  valueHeight?: number
  menuPosition?: MenuPosition
  menuPlacement?: MenuPlacement
}

interface CustomSelectProps {
  selectAll?: (selected: boolean) => void // Function to handle select all action
  selectedAll?: boolean // State to indicate if all options are selected
  withSelectAll?: boolean // Boolean to indicate if the "Select All" option should be shown
}

type SelectFieldProps = OwnProps & SelectProps<SelectOption, boolean, GroupBase<SelectOption>> & CustomSelectProps

const SelectComponent = forwardRef<React.ElementRef<typeof Select>, SelectFieldProps>(
  (
    {
      options,
      value,
      name,
      isClearable = true,
      isDisabled = false,
      isLoading = false,
      isSearchable = true,
      placeholder = '',
      hasError,
      onChange,
      onMenuOpen,
      onFocus,
      onInputChange,
      customStyle,
      components,
      menuHeight,
      valueHeight,
      menuPosition,
      menuPlacement,
      ...rest
    },
    ref,
  ) => {
    const containerRef = React.useRef(null)

    return (
      <Box ref={containerRef}>
        <Select
          ref={ref}
          value={value}
          onChange={onChange}
          options={options}
          name={name}
          isSearchable={isSearchable}
          isClearable={isClearable}
          isDisabled={isDisabled}
          isLoading={isLoading}
          placeholder={placeholder}
          onInputChange={onInputChange}
          onMenuOpen={onMenuOpen}
          onFocus={onFocus}
          styles={customStyle || getCustomSelectStyle({ hasError, menuHeight, valueHeight })}
          classNamePrefix="react-select"
          menuPosition={menuPosition || 'fixed'}
          menuPlacement={menuPlacement || 'auto'}
          closeMenuOnScroll={(e: Event) => (e.target as HTMLSelectElement).contains(containerRef.current)}
          components={components}
          {...rest}
        />
      </Box>
    )
  },
)

export default memo(SelectComponent)
